import React, { useCallback, useEffect, useRef, useMemo } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { IconAiOutlineDownload } from "@/utils/icons";
import html2canvas from "html2canvas";
import { lineBreakerForChartTitle } from "@/utils";

const ChartStackedColumnBar = (props: any) => {

  const { item } = props;
  const chartData = item?.data;
  const legendSchema = item?.legend_schema || [];
  const legendTitle = item?.legend_title || "";
  const in_form_k = item?.in_form_k || false;
  
  const processedData = useMemo(() => {
    if (!chartData) return [];
    return chartData.map((d: any) => ({
      ...d,
      category: legendSchema[d.c]?.name || d.c
    }));
  }, [chartData, legendSchema]);

  const uniqListOfX = [...new Set(chartData?.map((item: any) => item?.x || ''))];
  const customChartRef = useRef<HTMLDivElement>(null);

  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);


  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const renderChart = useCallback(() => {

    if (!chartRef.current || !processedData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 400 ? chartRef.current.offsetWidth : 400;

    const title = item?.title || "";
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": "",
      "width": chartWidth,
      "height": 400,
      "padding": { "top": 0, "bottom": 0, "left": 0, "right": 10 },
      "autosize": {
        "type": "fit",
        "contains": "padding"
      },
      "config": {
        "title": {
          "font": "Arial",
          "fontSize": 12
        },
        "text": {
          "font": "Arial",
          "fontSize": 10
        },
        "axis": {
          "labelFont": "Arial",
          "labelFontSize": "10",
          "labelColor": "gray"
        }
      },
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "data": [
        {
          "name": "table",
          "values": processedData,
          "transform": [
            {
              "type": "stack",
              "groupby": [
                "x"
              ],
              "sort": {
                "field": "c",
                "order": "descending"
              },
              "field": "y"
            },
            {
              "type": "formula",
              "as": "color_value",
              "expr": "datum.c === 0 ? datum.y0_color : datum.y1_color"
            }
          ]
        },
        {
          "name": "totals",
          "source": "table",
          "transform": [
            {
              "type": "aggregate",
              "groupby": ["x"],
              "ops": ["sum"],
              "fields": ["y"],
              "as": ["totalY"]
            }
          ]
        }
      ],
      "scales": [
        {
          "name": "x",
          "type": "band",
          "range": "width",
          "domain": {
            "data": "table",
            "field": "x"
          },
          "padding": 0.25,
          "round": true,
        },
        {
          "name": "y",
          "type": "linear",
          "range": "height",
          "nice": true,
          "zero": true,
          "domain": {
            "data": "table",
            "field": "y1"
          }
        },
        {
          "name": "color",
          "type": "ordinal",
          "range": {
            "data": "table",
            "field": "color_value"
          },
          "domain": {
            "data": "table",
            "field": "category"
          }
        }
      ],
      "axes": [
        {
          "orient": "bottom",
          "scale": "x",
          "labelLimit": { "signal": "bandwidth('x')" },
          "labelFontSize": 9,
          "labelColor": "black",
          "labelMultiline": true,
          "labelAlign": "center",
          "labelBaseline": "top"
        },
        {
          "orient": "left",
          "scale": "y",
          "zindex": 1
        }
      ],
      "marks": [
        {
          "type": "rect",
          "from": {
            "data": "table"
          },
          "encode": {
            "enter": {
              "x": {
                "scale": "x",
                "field": "x"
              },
              "width": {
                "scale": "x",
                "band": 1,
                "offset": -1
              },
              "y": {
                "scale": "y",
                "field": "y0"
              },
              "y2": {
                "scale": "y",
                "field": "y1"
              },
              "fill": {
                "scale": "color",
                "field": "category"
              }
            },
            "update": {
              "fillOpacity": {
                "value": 1
              }
            },
            "hover": {
              "fillOpacity": {
                "value": 0.5
              }
            }
          }
        },
        {
          "type": "text",
          "from": {
            "data": "table"
          },
          "encode": {
            "enter": {
              "x": { "scale": "x", "field": "x", "band": 0.5 },
              "y": {
                "signal": "scale('y', datum.y0 + (datum.y / 2))"
              },
              "text": {
                "signal": "datum.label_text ? datum.label_text : datum.y"
              },
              "align": {
                "value": "center"
              },
              "baseline": {
                "value": "middle"
              },
              "fill": {
                "value": "white"
              },
              "fontSize": {
                "value": 10
              },
              "fontWeight": {
                "value": "bold"
              }
            },
            "update": {
              "fillOpacity": {
                "signal": "datum.y > 0 ? 1 : 0"
              }
            }
          }
        },
        {
          "type": "text",
          "from": {
            "data": "totals"
          },
          "encode": {
            "enter": {
              "x": {
                "scale": "x",
                "field": "x",
                "band": 0.5
              },
              "y": {
                "scale": "y",
                "field": "totalY",
                "offset": -5
              },
              "text": {
                "signal": in_form_k ? "datum.totalY >= 1000 ? floor(datum.totalY / 1000) + 'k' : datum.totalY" : "datum.totalY"
              },
              "align": {
                "value": "center"
              },
              "baseline": {
                "value": "bottom"
              },
              "fill": {
                "value": "black"
              },
              "fontSize": {
                "value": 10
              },
              "fontWeight": {
                "value": "bold"
              }
            }
          }
        },
        // {
        //   "type": "text",
        //   "from": { "data": "table" },
        //   "encode": {
        //     "enter": {
        //       "x": { "scale": "x", "field": "x", "band": 0.5 },
        //       "y": { "signal": "height + 15" },
        //       "align": { "value": "center" },
        //       "baseline": { "value": "bottom" },
        //       "text": { "signal": "split(datum.x, ' ')" },
        //       "fontSize": { "value": 9 },
        //       "fill": { "value": "black" }
        //     }
        //   }
        // }
      ]
    }

    if (legendTitle && legendSchema.length > 0) {
      Object.assign(spec, {
        "legends": [
          {
            "fill": "color",
            "title": legendTitle,
            "orient": "right",
            // "orient": "bottom-right",
            "direction": "vertical",
            // "direction": "horizontal",
            "titleFontSize": 12,
            "titleFontFamily": "Arial",
            "titleColor": "black",
            "labelFontSize": 10,
            "labelFontFamily": "Arial",
            "labelColor": "black",
            "encode": {
              "labels": {
                "update": {
                  "opacity": { "value": 1 },
                }
              }
            }
          }
        ]
      });
    }

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      }
    }).then((stackedColumnBarView: any) => {
      viewRef.current = stackedColumnBarView.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [item, processedData, legendTitle, legendSchema]);


  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);


  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);

  const exportAsPNG = async () => {
    if (customChartRef.current) {
      const canvas = await html2canvas(customChartRef.current, { scale: 2 });
      const link = document.createElement('a');
      link.download = `${item?.title || 'table'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };



  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          // onClick={handleExport}
          onClick={exportAsPNG}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>

      <div ref={customChartRef}>
        <div id="chart-stacked-column-bar" className="!p-0 !pr-0 !pe-0 " ref={chartRef}></div>
        <div className="pb-2 pl-1">
          {uniqListOfX.map((item: any, index: number) => (
            <div key={item} className="mt-1 pb-[2px] leading-[7px] text-[10px] text-[#000000] ">
              <b>{index + 1}.</b> {item}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default ChartStackedColumnBar;
